// Canonical Beta Feedback question schema (Stage 4 — 2026-02).
//
// Language-agnostic: this file defines the SHAPE and LOGIC of the
// form (question ids, choice values, conditional gating, and
// "requires-comment" values). Localised labels/placeholders live
// in `./i18n.js`.
//
// Backend contract: each `question_id` is stored verbatim as
// `sections[].responses[].question_id`. `choice` values are stored
// verbatim as `sections[].responses[].choice`. Free-text answers
// are stored in `.comment`. Free-text-only questions are stored as
// `{question_id, comment}` (no choice/rating).
//
// The `requiresComment[]` list on each choice question drives BOTH:
//   • placeholder-copy switching (strong-prompt copy on required),
//   • conditional-comment validation (submit is blocked until every
//     visible required comment is filled in).
// These lists cover neutral, mixed, below-expectation, negative,
// uncertain, and potentially problematic responses (spec update
// 2026-02-16). Purely positive choices leave the comment optional.

export const SECTIONS = [
  {
    key: "overall_experience",
    questions: [
      {
        id: "oe_overall_rating",
        type: "choice",
        required: true,
        choices: ["excellent", "very_good", "good", "fair", "poor"],
        // "Fair" is below expectations; "Poor" is negative.
        requiresComment: ["fair", "poor"],
      },
      { id: "oe_best_part", type: "text" },
      { id: "oe_frustrating", type: "text" },
    ],
  },
  {
    key: "interview_questions",
    questions: [
      {
        id: "iq_relevance",
        type: "choice",
        required: true,
        choices: [
          "very_relevant",
          "mostly_relevant",
          "somewhat_relevant",
          "not_very_relevant",
          "not_relevant",
        ],
        // "Somewhat relevant" = mixed; "not very"/"not" = negative.
        requiresComment: ["somewhat_relevant", "not_very_relevant", "not_relevant"],
      },
      {
        id: "iq_realistic",
        type: "choice",
        required: true,
        choices: [
          "very_realistic",
          "mostly_realistic",
          "somewhat_realistic",
          "not_very_realistic",
          "not_realistic",
        ],
        requiresComment: [
          "somewhat_realistic",
          "not_very_realistic",
          "not_realistic",
        ],
      },
      {
        id: "iq_hard_wording",
        type: "choice",
        required: true,
        choices: ["no", "yes"],
        // "Yes" flags a potentially problematic experience.
        requiresComment: ["yes"],
      },
      {
        id: "iq_repetitive",
        type: "choice",
        required: true,
        choices: ["no", "yes"],
        requiresComment: ["yes"],
      },
    ],
  },
  {
    key: "assessment_report",
    questions: [
      {
        id: "ar_fair",
        type: "choice",
        required: true,
        choices: ["very_fair", "mostly_fair", "somewhat_fair", "not_very_fair", "not_fair"],
        requiresComment: ["somewhat_fair", "not_very_fair", "not_fair"],
      },
      {
        id: "ar_useful",
        type: "choice",
        required: true,
        choices: ["very_useful", "mostly_useful", "somewhat_useful", "not_very_useful", "not_useful"],
        requiresComment: ["somewhat_useful", "not_very_useful", "not_useful"],
      },
      {
        id: "ar_clarity",
        type: "choice",
        required: true,
        choices: ["yes_very_clearly", "mostly", "somewhat", "not_really", "no"],
        requiresComment: ["somewhat", "not_really", "no"],
      },
      {
        id: "ar_incorrect",
        type: "choice",
        required: true,
        choices: ["no", "yes", "unsure"],
        // Uncertainty ("unsure") and problem-signal ("yes") both
        // demand context.
        requiresComment: ["yes", "unsure"],
      },
    ],
  },
  {
    key: "suggested_improved_answers",
    questions: [
      {
        id: "sia_useful",
        type: "choice",
        required: true,
        choices: ["very_useful", "mostly_useful", "somewhat_useful", "not_very_useful", "not_useful"],
        requiresComment: ["somewhat_useful", "not_very_useful", "not_useful"],
      },
      {
        id: "sia_fabricated",
        type: "choice",
        required: true,
        choices: ["no", "yes", "unsure"],
        requiresComment: ["yes", "unsure"],
      },
    ],
  },
  {
    key: "study_guide",
    questions: [
      {
        id: "sg_reviewed",
        type: "choice",
        required: true,
        choices: ["yes", "no"],
        // Simple usage declaration — comment stays optional either way.
        requiresComment: [],
      },
      {
        id: "sg_useful",
        type: "choice",
        required: true,
        gatedBy: { id: "sg_reviewed", showWhen: ["yes"] },
        choices: ["very_useful", "mostly_useful", "somewhat_useful", "not_very_useful", "not_useful"],
        requiresComment: ["somewhat_useful", "not_very_useful", "not_useful"],
      },
      {
        id: "sg_seven_day_plan",
        type: "choice",
        required: true,
        gatedBy: { id: "sg_reviewed", showWhen: ["yes"] },
        choices: ["yes_definitely", "mostly", "somewhat", "not_really", "no"],
        requiresComment: ["somewhat", "not_really", "no"],
      },
    ],
  },
  {
    key: "re_practice",
    questions: [
      {
        id: "rp_used",
        type: "choice",
        required: true,
        choices: ["yes", "no"],
        requiresComment: [],
      },
      {
        id: "rp_helped",
        type: "choice",
        required: true,
        gatedBy: { id: "rp_used", showWhen: ["yes"] },
        choices: ["yes_significantly", "yes_somewhat", "a_little", "no"],
        requiresComment: ["a_little", "no"],
      },
      {
        id: "rp_feedback",
        type: "text",
        gatedBy: { id: "rp_used", showWhen: ["yes"] },
      },
      {
        id: "rp_why_not",
        type: "text",
        // Hard-coded rule (2026-02-16): when the tester answers "No"
        // to `rp_used`, the follow-up "What made you decide not to
        // use Re-Practice?" becomes REQUIRED. This is a targeted
        // exception to the general rule that text-only questions
        // are optional — see `isCommentMissing` / `QuestionBlock`
        // for the corresponding validation + UI wiring.
        required: true,
        gatedBy: { id: "rp_used", showWhen: ["no"] },
      },
    ],
  },
  {
    key: "ease_of_use",
    questions: [
      {
        id: "eu_ease",
        type: "choice",
        required: true,
        choices: ["very_easy", "mostly_easy", "somewhat_difficult", "very_difficult"],
        requiresComment: ["somewhat_difficult", "very_difficult"],
      },
      {
        id: "eu_issues",
        type: "choice",
        required: true,
        choices: ["no", "yes"],
        requiresComment: ["yes"],
      },
    ],
  },
  {
    key: "final_feedback",
    questions: [
      {
        id: "ff_use_again",
        type: "choice",
        required: true,
        choices: ["definitely", "probably", "maybe", "probably_not", "definitely_not"],
        // "Maybe" = uncertain — spec explicitly calls this out.
        requiresComment: ["maybe", "probably_not", "definitely_not"],
      },
      {
        id: "ff_recommend",
        type: "choice",
        required: true,
        choices: ["definitely", "probably", "maybe", "probably_not", "definitely_not"],
        requiresComment: ["maybe", "probably_not", "definitely_not"],
      },
      { id: "ff_improve", type: "text" },
      { id: "ff_anything_else", type: "text" },
    ],
  },
];

/**
 * Given a map of `{[question_id]: {choice?, comment?}}` values,
 * return the list of visible questions (respecting `gatedBy`).
 */
export function visibleQuestions(section, values) {
  return section.questions.filter((q) => {
    if (!q.gatedBy) return true;
    const parent = values[q.gatedBy.id];
    if (!parent || !parent.choice) return false;
    return q.gatedBy.showWhen.includes(parent.choice);
  });
}

/**
 * Returns `true` when picking `choiceValue` for this question turns
 * the free-text comment field into a required input. Drives BOTH
 * the strong-prompt placeholder copy and the submit-time validation.
 */
export function requiresComment(question, choiceValue) {
  if (!question || !choiceValue) return false;
  return (
    Array.isArray(question.requiresComment)
    && question.requiresComment.includes(choiceValue)
  );
}

// Legacy alias — kept so any historical callers (Stage 4 tests,
// downstream tooling) keep working after the 2026-02-16 rename.
export const isNegativeChoice = requiresComment;

/**
 * Returns the raw value (`{choice?, comment?}`) for a given
 * question id, treating null/undefined as an empty record. Purely
 * a readability helper for validation code.
 */
function _val(values, qid) {
  return (values && values[qid]) || {};
}

/**
 * Is the comment for this question missing when it's required?
 *
 * Handles two required-comment paths:
 *   1. Choice question whose current choice is in `requiresComment[]`.
 *   2. Text-only question with an explicit `required: true` flag
 *      (currently only `rp_why_not`, spec 2026-02-16).
 *
 * Used to gate submission (and to display the error ring only AFTER
 * the user attempts to submit).
 */
export function isCommentMissing(question, entry) {
  if (!question) return false;
  const comment = (entry && entry.comment) || "";
  const empty = comment.trim().length === 0;
  if (question.type === "text") {
    return Boolean(question.required) && empty;
  }
  if (question.type !== "choice") return false;
  const choice = entry && entry.choice;
  if (!requiresComment(question, choice)) return false;
  return empty;
}

/**
 * Returns `true` when every REQUIRED, VISIBLE question in the given
 * section has a valid answer AND every conditionally-required
 * comment / required-text field is filled in.
 */
export function isSectionComplete(section, values) {
  const visible = visibleQuestions(section, values);
  return visible.every((q) => {
    const v = _val(values, q.id);
    if (q.type === "choice" && q.required && !v.choice) return false;
    if (isCommentMissing(q, v)) return false;
    return true;
  });
}

/**
 * Collects the ids of every visible question whose comment is
 * currently required but empty. Used at submit time to steer the
 * user to the first missing required comment.
 */
export function collectMissingCommentIds(values) {
  const ids = [];
  for (const section of SECTIONS) {
    for (const q of visibleQuestions(section, values)) {
      if (isCommentMissing(q, _val(values, q.id))) {
        ids.push(q.id);
      }
    }
  }
  return ids;
}

/**
 * Serialise the state to the shape expected by
 * `POST /api/beta-feedback`. Only VISIBLE responses are sent so
 * hidden gated follow-ups never appear in the PDF.
 */
export function serialise(values) {
  const sections = [];
  for (const section of SECTIONS) {
    const visible = visibleQuestions(section, values);
    const responses = [];
    for (const q of visible) {
      const v = values[q.id];
      if (!v) continue;
      const row = { question_id: q.id };
      if (v.choice) row.choice = v.choice;
      if (v.comment != null && v.comment !== "") row.comment = v.comment;
      // Skip a completely empty text-only entry.
      if (!row.choice && !row.comment) continue;
      responses.push(row);
    }
    if (responses.length > 0) {
      sections.push({ section_key: section.key, responses });
    }
  }
  return sections;
}
